import { useCallback, useEffect, useState } from 'react';
import { Calendar, Gift, PawPrint } from 'lucide-react';
import toast from 'react-hot-toast';
import { getCustomerLoyalty, getCustomerMovements } from '../api/loyalty';
import { getErrorMessage } from '../api/response';
import Modal from './Modal';
import { LoadingState, EmptyState } from './PageState';
import { date, dateTime } from '../utils/format';

const MOVEMENT_LABELS = {
  earned: 'Patita ganada',
  adjustment: 'Ajuste manual',
  redeemed: 'Canje'
};

function movementLabel(movement) {
  if (movement.movement_type === 'earned' && movement.source === 'appointment') {
    return 'Patita por consulta pagada';
  }

  return MOVEMENT_LABELS[movement.movement_type] || movement.movement_type;
}

function movementDetail(movement) {
  if (movement.appointment_id && (movement.pet_name || movement.service_name)) {
    const parts = [
      movement.pet_name,
      movement.service_name,
      movement.appointment_starts_at ? dateTime(movement.appointment_starts_at) : null
    ].filter(Boolean);

    return parts.join(' · ');
  }

  return movement.description || 'Sin detalle';
}

const CustomerLoyaltyModal = ({ customer, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [movements, setMovements] = useState([]);

  const loadData = useCallback(async () => {
    if (!customer?.customer_id) return;

    try {
      setLoading(true);
      const [loyaltyData, movementData] = await Promise.all([
        getCustomerLoyalty(customer.customer_id),
        getCustomerMovements(customer.customer_id, { limit: 100 })
      ]);
      setLoyalty(loyaltyData);
      setMovements(Array.isArray(movementData) ? movementData : []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudieron cargar los movimientos'));
      setLoyalty(null);
      setMovements([]);
    } finally {
      setLoading(false);
    }
  }, [customer?.customer_id]);

  useEffect(() => {
    if (open && customer) {
      loadData();
    } else {
      setLoyalty(null);
      setMovements([]);
    }
  }, [open, customer, loadData]);

  const pawsRequired = loyalty?.paws_required ?? 14;
  const currentPaws = loyalty?.current_paws ?? customer?.current_paws ?? 0;

  return (
    <Modal
      open={open}
      title={`Movimientos · ${customer?.full_name || 'Cliente'}`}
      onClose={onClose}
      size="lg"
    >
      <div className="space-y-5">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <PawPrint className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-semibold text-gray-950">Progreso actual</p>
                <p className="text-xs text-gray-600">
                  Cada consulta pagada suma 1 patita. Al completar {pawsRequired} obtienes consulta gratis (vigencia 6 meses).
                </p>
              </div>
            </div>
            <p className="text-2xl font-bold text-blue-600">
              {currentPaws} <span className="text-sm font-medium text-gray-500">/ {pawsRequired}</span>
            </p>
          </div>
          {loyalty?.has_free_consultation && (
            <p className="mt-3 flex items-center gap-1 text-xs font-semibold text-green-700">
              <Gift className="h-3.5 w-3.5" />
              Tiene consulta gratis disponible para canjear
            </p>
          )}
        </div>

        {loading ? (
          <LoadingState rows={4} />
        ) : movements.length === 0 ? (
          <EmptyState
            title="Sin movimientos"
            message="Aún no hay patitas registradas. Se generan al pagar una cita o consulta."
          />
        ) : (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-700">Historial de movimientos</p>
            <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
              {movements.map((movement) => {
                const isEarned = movement.movement_type === 'earned';
                const points = Number(movement.points_amount) || 0;
                const sign = points >= 0 ? '+' : '';

                return (
                  <article
                    key={movement.id}
                    className="rounded-lg border border-gray-200 bg-white p-3 hover:bg-gray-50 transition"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-950">
                          {movementLabel(movement)}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">{movementDetail(movement)}</p>
                        {movement.appointment_id && (
                          <p className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                            <Calendar className="h-3 w-3 shrink-0" />
                            Cita #{movement.appointment_id}
                            {movement.appointment_status ? ` · ${movement.appointment_status}` : ''}
                          </p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p
                          className={`text-sm font-bold ${
                            isEarned ? 'text-blue-600' : points < 0 ? 'text-red-600' : 'text-gray-700'
                          }`}
                        >
                          {sign}
                          {points} {Math.abs(points) === 1 ? 'patita' : 'patitas'}
                        </p>
                        <p className="text-xs text-gray-400">{dateTime(movement.created_at)}</p>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        )}

        {loyalty?.rewards?.length > 0 && (
          <div>
            <p className="mb-2 text-sm font-semibold text-gray-700">Premios obtenidos</p>
            <div className="space-y-2">
              {loyalty.rewards.map((reward) => (
                <div key={reward.id} className="rounded-lg border border-green-200 bg-green-50 p-3">
                  <p className="text-sm font-semibold text-gray-950">{reward.name}</p>
                  <p className="text-xs text-gray-600">
                    Obtenido: {date(reward.earned_at)} · Vence: {date(reward.expires_at)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CustomerLoyaltyModal;
